import { gsap } from "gsap";
import { LAYOUT_MOBILE, STATUS } from "../Constants";
import {
    ANIMATION_CLEAR_PROPS,
    DEFAULT_SCENE_TRANSITION_DURATION,
    parseSceneTransitionSettings,
    POPUP_TRANSITIONS,
    SCENE_TRANSITIONS,
} from "./sceneMotionConfig";

export default class SceneTransitions {
    constructor(spread) {
        this.spread = spread;
        this.activeTimeline = null;
    }

    isAnimating() {
        return !!this.activeTimeline;
    }

    prefersReducedMotion() {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    killActiveTimeline() {
        if (this.activeTimeline) {
            this.activeTimeline.kill();
            this.activeTimeline = null;
        }
    }

    getDuration(duration = DEFAULT_SCENE_TRANSITION_DURATION, forcedDuration) {
        return this.prefersReducedMotion() ? 0 : (forcedDuration ?? duration)
    }

    getSceneTransitionName() {
        if (this.spread.active_layout === LAYOUT_MOBILE) {
            // old published data has no mobile scenestransition attribute; default
            // to "none" so those spreads keep their instant swap instead of the
            // "scroll" preset fallback
            return this.spread.runSelector(".layout.mobile").data("scenestransition") ?? "none";
        }
        return this.spread.runSelector(".layout.desktop").data("scenestransition");
    }

    getScenePreset(name, reverse) {
        const direction = reverse ? -1 : 1;
        // Own-property lookup: a published name like "constructor" would otherwise resolve to an
        // Object.prototype member (truthy) and bypass the fallback.
        const createPreset = Object.prototype.hasOwnProperty.call(SCENE_TRANSITIONS, name)
            ? SCENE_TRANSITIONS[name]
            : SCENE_TRANSITIONS.scroll;

        return createPreset(direction);
    }

    getPopupPreset(nameOrSettings) {
        const settings = typeof nameOrSettings === "string"
            ? { type: nameOrSettings }
            : (nameOrSettings || { type: "move" });
        // Own-property lookup: see getScenePreset.
        const createPreset = Object.prototype.hasOwnProperty.call(POPUP_TRANSITIONS, settings.type)
            ? POPUP_TRANSITIONS[settings.type]
            : POPUP_TRANSITIONS.move;

        return createPreset(settings);
    }

    getPopupAnimationTargets(sceneElement) {
        const contentElement = sceneElement.querySelector('.maglrspread');

        return {
            contentElement,
            animatedElements: contentElement ? [sceneElement, contentElement] : [sceneElement],
        };
    }

    transitionToScene(selector, skipAnimation = false) {
        if (this.isAnimating()) {
            console.debug("Already animating. return false.");
            return false;
        }

        const newCurrent = this.spread.runSelector(selector);
        const oldCurrent = this.spread.runSelector(".current_scene");

        if(newCurrent.length === 0){
            console.warn("transition to unknown scene", selector);
            return false;
        }
        if(newCurrent.is(oldCurrent)){
            console.warn("transition to already current scene", selector);
            return false;
        }

        const transitionName = this.getSceneTransitionName();
        const reverse = newCurrent.data('scene-index') < oldCurrent.data('scene-index');
        const preset = this.getScenePreset(transitionName, reverse);

        this.spread.pageTransitions = {
            old_ani_ended: false,
            new_ani_ended: false,
            isAnimating: true,
            inClass: 'transition_scene',
            outClass: ''
        };

        this.spread.shiftScrollToScene(this.spread.currentScene);

        if (skipAnimation || preset.duration === 0 || this.prefersReducedMotion()) {
            this.spread.onEndAnimation(oldCurrent, newCurrent);
            return true;
        }

        const oldElement = oldCurrent[0];
        const newElement = newCurrent[0];
        const layout = this.spread.getLayoutContainer();
        const duration = this.getDuration(preset.duration);

        layout.classList.add("scenesInTransition");
        newCurrent.addClass(this.spread.pageTransitions.inClass);

        gsap.set([oldElement, newElement], { clearProps: ANIMATION_CLEAR_PROPS });
        gsap.set(newElement, preset.newFrom || {});
        if (preset.oldFrom) {
            gsap.set(oldElement, preset.oldFrom);
        }

        // the mobile layout is sized by its active scene; animate it towards the
        // incoming scene's natural height so the page doesn't jump afterwards
        const newLayoutHeight = this.spread.active_layout === LAYOUT_MOBILE
            && layout.offsetHeight !== newElement.offsetHeight
            ? newElement.offsetHeight
            : null;
        if (newLayoutHeight !== null) {
            gsap.set(layout, { height: layout.offsetHeight });
        }

        this.activeTimeline = gsap.timeline({
            defaults: {
                duration,
                ease: preset.ease || 'power2.inOut',
            },
            onComplete: () => {
                this.activeTimeline = null;
                gsap.set([oldElement, newElement], { clearProps: ANIMATION_CLEAR_PROPS });
                gsap.set(layout, { clearProps: "height" });
                this.spread.onEndAnimation(oldCurrent, newCurrent);
                layout.classList.remove("scenesInTransition");
            }
        });

        if (preset.oldTo) {
            this.activeTimeline.to(oldElement, preset.oldTo, 0);
        }
        if (preset.newTo) {
            this.activeTimeline.to(newElement, preset.newTo, 0);
        }
        if (newLayoutHeight !== null) {
            this.activeTimeline.to(layout, { height: newLayoutHeight }, 0);
        }

        return true;
    }

    getPopupTransitionName(sceneElement) {
        return this.getPopupTransitionSettings(sceneElement).type;
    }

    getPopupTransitionSettings(sceneElement) {
        return parseSceneTransitionSettings(sceneElement.dataset.scenetransition);
    }

    openPopup(sceneElement, targetScene, forcedDuration) {
        this.killActiveTimeline();

        // before the scrollTo(0, 0) below: the scroll effects scrub on the document scroll
        // and would snap the visually frozen background scenes to their scroll-0 state
        this.spread.scrollingSceneEffects.freeze();

        if (this.spread._scrollBeforePopup === undefined) {
            this.spread._scrollBeforePopup = this.spread.getScrollTop();
        }

        this.spread.scenes.forEach(scene => {
            scene.setTopForPopup()
        });

        this.spread.scenes.forEach(scene => {
            if (scene !== targetScene) {
                if (scene.isPopup) {
                    scene.stop();
                    const otherPopup = scene.getSceneElement(false);
                    const { animatedElements } = this.getPopupAnimationTargets(otherPopup);
                    otherPopup.classList.remove('scene_active');
                    this.spread.popupA11y.deactivate(otherPopup, { restoreFocus: false });
                    gsap.set(animatedElements, { clearProps: ANIMATION_CLEAR_PROPS });
                } else {
                    scene.pause();
                }
            }
        });

        this.spread.setCurrentSceneOnly(targetScene);
        sceneElement.classList.add('scene_active');

        const popupScrollTop = this.spread.scrollContext.element
            ? this.spread.scrollContext.offsetOf(this.spread.getSpreadElement(false))
            : this.spread.getSpreadElement(false).offsetTop ?? 0;
        this.spread.scrollContext.scrollTo(popupScrollTop, 'instant');

        const preset = this.getPopupPreset(this.getPopupTransitionSettings(sceneElement));
        const duration = this.getDuration(preset.duration, forcedDuration);
        const { contentElement, animatedElements } = this.getPopupAnimationTargets(sceneElement);

        // activate before the animation (and the duration-0 early return): a11y
        // must be live even when the open timeline gets killed halfway
        this.spread.popupA11y.activate(sceneElement, contentElement);

        if (duration === 0) {
            gsap.set(sceneElement, preset.sceneTo);
            if (contentElement) {
                gsap.set(contentElement, preset.contentTo);
            }
            gsap.set(animatedElements, { clearProps: ANIMATION_CLEAR_PROPS });
            if (sceneElement.classList.contains('scene_active')) {
                targetScene.start();
            }
            return Promise.resolve();
        }

        gsap.set(animatedElements, { clearProps: ANIMATION_CLEAR_PROPS });
        gsap.set(sceneElement, preset.sceneFrom);
        if (contentElement) {
            gsap.set(contentElement, preset.contentFrom);
        }

        return new Promise(resolve => {
            this.activeTimeline = gsap.timeline({
                onComplete: () => {
                    this.activeTimeline = null;
                    gsap.set(animatedElements, { clearProps: ANIMATION_CLEAR_PROPS });
                    if (sceneElement.classList.contains('scene_active')) {
                        targetScene.start();
                    }
                    resolve();
                },
                // killActiveTimeline() (e.g. ESC/backdrop-close mid-open) kills the
                // timeline without firing onComplete; resolve here so an awaiting
                // multiActionLink chain does not hang forever
                onInterrupt: () => resolve()
            });

            this.activeTimeline.to(sceneElement, {
                ...preset.sceneTo,
                duration,
                ease: preset.easeIn || 'power2.out',
            }, 0);

            if (contentElement) {
                this.activeTimeline.to(contentElement, {
                    ...preset.contentTo,
                    duration,
                    ease: preset.easeIn || 'power2.out',
                }, 0);
            }
        });
    }

    closePopup(sceneElement, scene, forcedDuration) {
        this.killActiveTimeline();

        const preset = this.getPopupPreset(this.getPopupTransitionSettings(sceneElement));
        const duration = this.getDuration(preset.duration, forcedDuration);
        const { contentElement } = this.getPopupAnimationTargets(sceneElement);
        const finish = () => this.finishPopupClose(scene, sceneElement);

        if (duration === 0) {
            finish();
            return Promise.resolve();
        }

        return new Promise(resolve => {
            this.activeTimeline = gsap.timeline({
                onComplete: () => {
                    this.activeTimeline = null;
                    finish();
                    resolve();
                },
                // see openPopup: resolve on kill so a chained await does not hang
                onInterrupt: () => resolve()
            });

            this.activeTimeline.to(sceneElement, {
                ...preset.sceneOut,
                duration,
                ease: preset.easeOut || 'power2.in',
            }, 0);

            if (contentElement) {
                this.activeTimeline.to(contentElement, {
                    ...preset.contentOut,
                    duration,
                    ease: preset.easeOut || 'power2.in',
                }, 0);
            }
        });
    }

    finishPopupClose(scene, sceneElement) {
        scene.stop();
        sceneElement.classList.remove('scene_active');
        gsap.set(this.getPopupAnimationTargets(sceneElement).animatedElements, { clearProps: ANIMATION_CLEAR_PROPS });

        this.spread.scenes.forEach(backgroundScene => {
            backgroundScene.getSceneElement(false).style.removeProperty('top');
        });

        if (this.spread._scrollBeforePopup !== undefined) {
            this.spread.scrollContext.scrollTo(this.spread._scrollBeforePopup, 'instant');
            delete this.spread._scrollBeforePopup;
        }

        this.spread.scenes.forEach(backgroundScene => {
            if (backgroundScene.status === STATUS.PAUSED) {
                this.spread.setCurrentSceneOnly(backgroundScene);
                backgroundScene.start();
            }
        });

        // after the scroll restore: unfreeze refreshes the triggers against the scroll position
        this.spread.scrollingSceneEffects.unfreeze();

        this.spread.popupA11y.deactivate(sceneElement, { restoreFocus: true });
    }

    // Spread.stop() with a popup still open: tear the popup down synchronously.
    // finishPopupClose() minus the pieces that assume the reader keeps looking at
    // the page — no animation, no focus restore, and no start() on the paused
    // background scenes (stop() stops every scene right after this, and start()
    // would fire embed/video/autoplay side effects on a page that just went
    // invisible). The scroll-effect freeze needs no counterpart here:
    // stopScenes() kills the triggers, which also resets the frozen flag.
    abortPopup() {
        const sceneElement = this.spread.runSelector('.scene.popup_scene.scene_active')[0];
        if (!sceneElement) {
            return;
        }

        this.killActiveTimeline();

        // dropping scene_active releases the CSS position:fixed on the background
        // scenes, so the scroll restore below happens against the normal layout
        sceneElement.classList.remove('scene_active');
        gsap.set(this.getPopupAnimationTargets(sceneElement).animatedElements, { clearProps: ANIMATION_CLEAR_PROPS });

        this.spread.scenes.forEach(backgroundScene => {
            backgroundScene.getSceneElement(false).style.removeProperty('top');
            if (backgroundScene.status === STATUS.PAUSED) {
                this.spread.setCurrentSceneOnly(backgroundScene);
            }
        });

        // a popup opened as deeplink never paused a background scene; fall back to
        // the first regular scene so stopScenes() does not mark the popup as
        // first_scene and reopen it on the next start()
        if (this.spread.currentScene && this.spread.currentScene.isPopup) {
            const fallback = this.spread.scenes.find(scene => !scene.sticky && !scene.isPopup);
            fallback && this.spread.setCurrentSceneOnly(fallback);
        }

        if (this.spread._scrollBeforePopup !== undefined) {
            this.spread.scrollContext.scrollTo(this.spread._scrollBeforePopup, 'instant');
            delete this.spread._scrollBeforePopup;
        }

        this.spread.popupA11y.deactivate(sceneElement, { restoreFocus: false });
    }
}
